import { useState, useEffect, useRef, createContext } from 'react'
import StripePortalDialog from '@/account/components/StripePortalDialog'
import { setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import PartnerCodeDialog from '@/checkout/components/PartnerCodeDialog'
import signOutOfApp from '@/firebase/auth/sign-out'
import { useNavigate } from 'react-router'
import CheckoutContainer from '@/checkout/components/CheckoutContainer'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import createStripeSetupIntent from '@/checkout/fetch/post-stripe-setup-intent'
import type { CreateStripeSetupIntentResponse } from '@/checkout/fetch/post-stripe-setup-intent'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { ArrowUpRight, AlertErrorCircle } from '@/assets/icons'
import { setLegacyStripeCheckoutVisible } from '@/store/slices/ui-state.slice'
import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import { annualBillingMessage, monthlyBillingMessage } from '@/checkout/constants'
import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import BillingCycleToggle from '@/checkout/components/BillingCycleToggle'
import { setBillingCycle } from '@/checkout/store/slice'
import DueSection, { CheckoutType } from './DueSection'
import { selectAnnualBillingSavingsPercent, selectMonthlyPriceForPlan } from '../store/selectors'
import { formatCentsToDollarsCompact } from '@/util/money'

// Create a context to share the scroll function
export interface ResignupScrollContextType {
  scrollToTop: () => void
}

export const ResignupScrollContext = createContext<ResignupScrollContextType>({
  // No-op function as default value
  scrollToTop: () => {
    /* Default empty implementation */
  },
})

export default function TestimonialPlusResignup() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { toggleWidget } = useHubspotConversations()

  // Local state
  const [partnerCodeDialogVisible, setPartnerCodeDialogVisible] = useState(false)
  const [stripeSetupIntent, setStripeSetupIntent] = useState<string | null>(null)
  const [setupIntentLoading, setSetupIntentLoading] = useState(false)

  // Redux state
  const billingCycle = useAppSelector((state: RootState) => state.checkoutState.billingCycle)
  const planMonthlyPrice = useAppSelector((state: RootState) => selectMonthlyPriceForPlan(state, { billingCycle }))
  const annualPlanSavingsPercent = useAppSelector((state: RootState) => selectAnnualBillingSavingsPercent(state))

  const showStripePortalButton = useAppSelector((state: RootState) => state.uiState.showLegacyStripeCheckout)
  const legacyStripeCheckoutMessage = useAppSelector((state: RootState) => state.uiState.legacyStripeCheckoutMessage)

  // Create the ref inside the component
  const resignupScrollContainerRef = useRef<HTMLDivElement>(null)

  // Function to scroll to the top of the container
  const scrollToTop = () => {
    if (resignupScrollContainerRef.current) {
      resignupScrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (stripeSetupIntent) return
    fetchStripeSetupIntent()
  }, [stripeSetupIntent])

  const fetchStripeSetupIntent = async () => {
    setSetupIntentLoading(true)
    console.log('fetchStripeSetupIntent', stripeSetupIntent)
    try {
      const response: CreateStripeSetupIntentResponse = await createStripeSetupIntent()
      setStripeSetupIntent(response.client_secret ?? null)
      setSetupIntentLoading(false)
    } catch {
      dispatch(setLegacyStripeCheckoutVisible(true))
    }
  }

  const handleBillingCycleSelect = (cycle: BillingCycle) => {
    dispatch(setBillingCycle(cycle))
  }

  const paymentSubHeader = `Restart your account at ${formatCentsToDollarsCompact(planMonthlyPrice)} / month, ${(billingCycle === BillingCycle.ANNUAL
    ? annualBillingMessage
    : monthlyBillingMessage
  ).replace('{discountPercentage}', annualPlanSavingsPercent.toString())} Cancel anytime.`

  return (
    <ResignupScrollContext.Provider value={{ scrollToTop }}>
      <div className="flex h-full flex-col md:flex-row">
        <div className="flex md:w-1/2 bg-brand-neutral-50">
          <div ref={resignupScrollContainerRef} className="flex flex-col space-y-4 justify-center w-full overflow-y-auto">
            <PartnerCodeDialog visible={partnerCodeDialogVisible} onClose={() => setPartnerCodeDialogVisible(false)} />
            <StripePortalDialog />
            <div className="sm:mx-auto w-full h-full sm:max-w-xl flex justify-center">
              <div className="bg-brand-neutral-50 p-8 max-w-md sm:rounded-lg">
                <div className="flex items-center justify-between">
                  <h1 className="text-[23px] md:text-[27px] text-start font-bold tracking-tight text-brand-neutral-900">Payment Required</h1>
                </div>
                <h2 className="text-l mb-2 text-start font-semibold text-brand-neutral-800">{paymentSubHeader}</h2>
                <h2 className="text-2xs mb-3 text-start text-brand-neutral-500">*Your account is currently canceled and you are not being billed.</h2>
                <img src="/images/logos/stripe-logo-svg.svg" alt="Logo 1" className="mb-3 h-7 flex" />
                <DueSection checkoutType={CheckoutType.RESIGNUP} />
                <BillingCycleToggle billingCycle={billingCycle} handleBillingCycleSelect={handleBillingCycleSelect} />
                {setupIntentLoading ? (
                  <div className="mt-5 text-center">
                    <CircularProgressContinuous />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col justify-between text-center text-sm text-brand-neutral-500">
                      {legacyStripeCheckoutMessage && (
                        <div className="mt-3 p-3 bg-white border border-red-400 rounded-md shadow-xs mb-4">
                          <p className="text-red-600 flex items-start text-left">
                            <AlertErrorCircle className="h-5 w-5 mr-1" />
                            {legacyStripeCheckoutMessage}
                          </p>
                        </div>
                      )}
                      {showStripePortalButton ? (
                        <>
                          <button
                            type="submit"
                            className="w-full rounded-md bg-brand-700 px-2 py-1 mt-4 text-lg  text-white shadow-xs hover:bg-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-900 "
                            onClick={() => dispatch(setStripePortalDialogVisible(true))}
                          >
                            Add Payment Method on Stripe <ArrowUpRight className={'inline-block mb-1'} />
                          </button>
                        </>
                      ) : (
                        <>
                          <CheckoutContainer checkoutButtonCopy={'Restart account'} clientSecret={stripeSetupIntent} />
                        </>
                      )}
                    </div>
                    <div className="flex justify-between gap-x-4 mt-5 text-center text-sm text-brand-neutral-500">
                      <button
                        onClick={() => {
                          signOutOfApp(navigate)
                        }}
                        className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
                      >
                        Sign out
                      </button>
                      <button
                        onClick={() => {
                          toggleWidget()
                        }}
                        className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
                      >
                        Chat with support
                      </button>
                      <button
                        onClick={() => {
                          setPartnerCodeDialogVisible(true)
                        }}
                        className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
                      >
                        Partner code
                      </button>
                    </div>
                    <div className="h-4" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex md:w-1/2">
          <div className="flex flex-col py-8 justify-center w-full bg-brand-neutral-100 px-6">
            <div className="flex justify-center w-full">
              <ul role="list" className="-mb-8">
                <li>
                  <div className="flex items-center gap-x-6">
                    <img className="h-16 w-16 rounded-full" src="/images/testimonial-headshot-web.jpeg" alt="" />
                    <div>
                      <h3 className="text-base font-semibold leading-7 tracking-tight text-brand-neutral-900">Emily L.</h3>
                      <p className="mt-1 italic">"I use Paxton AI to get up to speed on issues and generate first drafts in minutes instead of hours."</p>
                    </div>
                  </div>
                </li>

                <li className="mt-8">
                  <div className="h-5" />
                  <h3 className="font-bold mb-2">Featured In</h3>
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 items-center">
                    <img src="/images/logos/reuters-logo-svg.svg" alt="Logo 1" className="h-8 pr-2" />
                    <img src="/images/logos/fortune-logo-svg.svg" alt="Logo 2" className="h-9 p-2" />
                    <img src="/images/logos/forbes-logo-svg.svg" alt="Logo 2" className="h-11" />
                    <img src="/images/logos/axios-logo-svg.svg" alt="Logo 2" className="h-5" />
                    <div className={'basis-full h-0'}></div>
                    <img src="/images/logos/wsj-logo-svg.svg" alt="Logo 2" className="h-5" />
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ResignupScrollContext.Provider>
  )
}
